import { Box, Flex, Text, HStack, Badge } from '@chakra-ui/react';
import { Building2, Square, Box as Box3D, CornerUpRight, Grid3x3, Magnet, LocateFixed, Terminal, DraftingCompass, FileText } from 'lucide-react';
import * as THREE from 'three';
import { useTool, useSelection, useMode } from '../../state';
// Direct import: the snapping barrel also exports the drei-based SnapIndicator,
// which would pin drei/fiber into this eagerly-mounted component's chunk.
import { useSnapStore } from '../../tools/snapping/SnapManager';
import { useAppStore } from '../../state/store';
import { useTranslation } from 'react-i18next';
import { useLayoutStore } from '../../state/useLayoutStore';
import { McpStatusBadge } from '../mcp/McpBridgeHost';
import { LanguageSwitcher } from './LanguageSwitcher';
import { IconToggle } from './IconToggle';
import { SnapTypeToggles } from './SnapTypeToggles';
import { ViewControlBar } from '../../ui/ViewControlBar';
import { BimToolPrompt } from '../../ui/BimToolPrompt';

/** Fallback for the coordinate readout before the cursor has entered the
 *  viewport (module scope: never re-allocated per render). */
const ORIGIN_COORDS = new THREE.Vector3(0, 0, 0);

/** Icon-only mode indicator: neutral gray, styled like every other status
 *  bar icon; the full mode name stays available via the hover tooltip. */
const MODE_ICONS: Record<string, { Icon: typeof Building2; labelKey: 'modes.bim' | 'modes.2d' | 'modes.3d' }> = {
    bim: { Icon: Building2, labelKey: 'modes.bim' },
    '2d': { Icon: Square, labelKey: 'modes.2d' },
    '3d': { Icon: Box3D, labelKey: 'modes.3d' },
};

/** MODEL / PAPER SPACE toggle with a layout switcher (Faz 9 paper space),
 *  moved here from the 2D viewport's top-right corner overlay. Icon-only:
 *  drafting compass for MODEL, sheet for PAPER; the active layout name
 *  stays available via the hover tooltip. */
function ModelSpaceToggle() {
    const { t } = useTranslation();
    const activeId = useLayoutStore((s) => s.activeId);
    const layouts = useLayoutStore((s) => s.layouts);
    const active = layouts.find((l) => l.id === activeId);
    return (
        <IconToggle
            icon={active ? FileText : DraftingCompass}
            on={!active}
            title={active ? `${active.name} (PAPER) — ${t('statusbar.toggleModelSpace')}` : t('statusbar.toggleModelSpace')}
            onClick={() => {
                // Cycle: Model -> Layout1 -> Layout2 -> ... -> Model
                if (!active) {
                    if (layouts.length > 0) useLayoutStore.getState().setActive(layouts[0].id);
                    else {
                        useLayoutStore.getState().addLayout();
                        const next = useLayoutStore.getState().layouts.slice(-1)[0];
                        if (next) useLayoutStore.getState().setActive(next.id);
                    }
                    return;
                }
                const idx = layouts.findIndex((l) => l.id === activeId);
                const next = idx + 1 < layouts.length ? layouts[idx + 1] : undefined;
                useLayoutStore.getState().setActive(next ? next.id : null);
            }}
        />
    );
}

/** One-pixel vertical divider matching the bar's border token — the same
 *  separator language as the BIM View Control Bar. Stays fixed while the
 *  neighboring snap row scrolls internally. */
function StatusSeparator() {
    return <Box w="1px" h="16px" bg="border.base" flexShrink={0} aria-hidden="true" />;
}

/**
 * Status Bar Component
 *
 * Displays:
 * - Current coordinates (from SnapManager)
 * - Active tool name
 * - Selection count
 * - Mode indicator
 * - 3D mode: clickable draft-mode slots (Grid / Ortho / Osnap / Plan)
 *   mirroring the 3D status line; each maps to the shared actionIds so
 *   command-line and button flows stay identical.
 */
export function StatusBar() {
    const mode = useMode();
    const tool = useTool();
    const selectedIds = useSelection();
    const { t } = useTranslation();

    // Coordinate readout: the point the NEXT click will commit (osnap target
    // when one is acquired, else the constrained cursor), published by the
    // shared drafting pipeline. 2D behavior — the readout tracks the cursor
    // live and always matches the viewport snap marker.
    const draftCursor = useSnapStore((s) => s.draftCursor);
    const coords = draftCursor ?? ORIGIN_COORDS;
    const gridVisible = useAppStore((s) => s.view.gridVisible);
    const orthoMode = useAppStore((s) => s.orthoMode);
    const osnapEnabled = useSnapStore((s) => s.enabled);
    const gridSnapEnabled = useSnapStore((s) => s.gridSnapEnabled);
    const commandLineOpen = useAppStore((s) => s.commandLineOpen);
    const shiftOrthoActive = useAppStore((s) => s.shiftOrthoActive);

    return (
        <Box
            h="24px"
            // Shared theme tokens (bg.toolbar / border.base) so the bar reads
            // identically across every drafting workbench.
            bg="bg.toolbar"
            borderTop="1px solid"
            borderColor="border.base"
            px={3}
        >
            <Flex h="100%" align="center" justify="space-between">
                {/* Left - Coordinates (hidden in BIM mode: no drafting
                    coordinate readout there) */}
                {mode !== 'bim' && (
                    <HStack spacing={4}>
                        <Text fontSize="xs" color="text.secondary" fontFamily="mono">
                            X: {coords.x?.toFixed(2) || '0.00'}
                        </Text>
                        <Text fontSize="xs" color="text.secondary" fontFamily="mono">
                            Y: {coords.y?.toFixed(2) || '0.00'}
                        </Text>
                        <Text fontSize="xs" color="text.secondary" fontFamily="mono">
                            Z: {coords.z?.toFixed(2) || '0.00'}
                        </Text>
                    </HStack>
                )}

                {/* Center - Tool & Selection. BIM mode also hosts the active
                    tool prompt (+ hover status text) moved from the removed
                    mode-level status bar. */}
                <HStack spacing={3} minW={0}>
                    {mode === 'bim' && <BimToolPrompt />}
                    <Badge size="sm" variant="subtle" colorScheme="gray">
                        {tool.charAt(0).toUpperCase() + tool.slice(1)}
                    </Badge>
                    {selectedIds.length > 0 && (
                        <Text fontSize="xs" color="text.secondary">
                            {t('common.selectedCount', { count: selectedIds.length })}
                        </Text>
                    )}
                </HStack>

                {/* Right - Mode Indicator. The drafting constraint family
                    (ORTHO + OSNAP master [+ grid snap]) is set apart from the
                    neighboring view toggles by a left separator, matching the
                    snap-type row's own delimiters. */}
                <HStack spacing={2}>
                    {/* 3D workbench: the shared View Control Bar (visual style
                        + projection — the flags the CAD renderer consumes),
                        then the grid toggle and the drafting constraint
                        family behind their own left separators. */}
                    {mode === '3d' && (
                        <>
                            <ViewControlBar embedded />
                            <StatusSeparator />
                            <IconToggle
                                icon={Grid3x3}
                                on={gridVisible}
                                title={t('statusbar.toggleGridF7')}
                                onClick={() => useAppStore.getState().updateView({ gridVisible: !gridVisible })}
                            />
                            <StatusSeparator />
                            <HStack spacing={1} flexShrink={0}>
                                <IconToggle
                                    icon={CornerUpRight}
                                    on={orthoMode}
                                    title={t('statusbar.toggleOrthoF1F8')}
                                    onClick={() => useAppStore.getState().toggleOrthoMode()}
                                />
                                <IconToggle
                                    icon={Magnet}
                                    on={osnapEnabled}
                                    title={t('statusbar.toggleOsnapF3')}
                                    onClick={() => { const s = useSnapStore.getState(); s.setEnabled(!s.enabled); }}
                                />
                                <IconToggle
                                    icon={LocateFixed}
                                    on={gridSnapEnabled}
                                    title={t('statusbar.toggleGridSnapF9')}
                                    onClick={() => useSnapStore.getState().toggleGridSnap()}
                                />
                            </HStack>
                        </>
                    )}
                    {/* 2D workbench: view toggles first, then the drafting
                        OSNAP/ORTHO pair behind its own left separator. */}
                    {mode === '2d' && (
                        <>
                            <HStack spacing={1} flexShrink={0}>
                                <IconToggle
                                    icon={Grid3x3}
                                    on={gridVisible}
                                    title={t('statusbar.toggleGridF7')}
                                    onClick={() => useAppStore.getState().updateView({ gridVisible: !gridVisible })}
                                />
                                <ModelSpaceToggle />
                            </HStack>
                            <StatusSeparator />
                            <HStack spacing={1} flexShrink={0}>
                                <IconToggle
                                    icon={Magnet}
                                    on={osnapEnabled}
                                    title={t('statusbar.toggleOsnapF3')}
                                    onClick={() => { const s = useSnapStore.getState(); s.setEnabled(!s.enabled); }}
                                />
                                <IconToggle
                                    icon={CornerUpRight}
                                    on={orthoMode}
                                    title={t('statusbar.toggleOrthoF1F8')}
                                    onClick={() => useAppStore.getState().toggleOrthoMode()}
                                />
                            </HStack>
                        </>
                    )}
                    {/* BIM workbench: the viewport's former floating View
                        Control Bar lives inline here, plus the F1 ORTHO
                        toggle (lights up also while Shift is held) and the
                        OSNAP master switch (BIM tools resolve points through
                        the shared OSNAP pass). */}
                    {mode === 'bim' && (
                        <>
                            <ViewControlBar embedded />
                            <StatusSeparator />
                            <HStack spacing={1} flexShrink={0}>
                                <IconToggle
                                    icon={CornerUpRight}
                                    on={orthoMode || shiftOrthoActive}
                                    title={t('statusbar.toggleOrthoF1F8')}
                                    onClick={() => useAppStore.getState().toggleOrthoMode()}
                                />
                                <IconToggle
                                    icon={Magnet}
                                    on={osnapEnabled}
                                    title={t('statusbar.toggleOsnapF3')}
                                    onClick={() => { const s = useSnapStore.getState(); s.setEnabled(!s.enabled); }}
                                />
                            </HStack>
                        </>
                    )}
                    {/* Sketch workbench: mirrors the BIM right side — the
                        mode-aware View Control Bar (visual style drives the
                        sketch display styles, shadows the sketch sun, plan/
                        projection the sketch camera), the grid toggle, then
                        the drafting constraint family. The sketch inference
                        ladder honors the shared ORTHO flag; the master OSNAP
                        switch gates it like every other workbench. */}
                    {mode === 'sketch' && (
                        <>
                            <ViewControlBar embedded />
                            <StatusSeparator />
                            <IconToggle
                                icon={Grid3x3}
                                on={gridVisible}
                                title={t('statusbar.toggleGridF7')}
                                onClick={() => useAppStore.getState().updateView({ gridVisible: !gridVisible })}
                            />
                            <StatusSeparator />
                            <HStack spacing={1} flexShrink={0}>
                                <IconToggle
                                    icon={CornerUpRight}
                                    on={orthoMode}
                                    title={t('statusbar.toggleOrthoF1F8')}
                                    onClick={() => useAppStore.getState().toggleOrthoMode()}
                                />
                                <IconToggle
                                    icon={Magnet}
                                    on={osnapEnabled}
                                    title={t('statusbar.toggleOsnapF3')}
                                    onClick={() => { const s = useSnapStore.getState(); s.setEnabled(!s.enabled); }}
                                />
                            </HStack>
                        </>
                    )}
                    {/* Shared per-type OSNAP switches (AutoCAD status-bar
                        style): one icon row for every workbench, flanked by
                        separators so the family reads as its own group. Sits
                        outside the mode groups so it can compress + scroll
                        internally while the fixed toggles keep flex-shrink-0. */}
                    <StatusSeparator />
                    <SnapTypeToggles />
                    <StatusSeparator />
                    {(mode === '2d' || mode === '3d') && (
                        <IconToggle
                            icon={Terminal}
                            on={commandLineOpen}
                            title={t('statusbar.toggleCommandLine')}
                            onClick={() => useAppStore.getState().toggleCommandLine()}
                        />
                    )}
                    <LanguageSwitcher compact />
                    <McpStatusBadge />
                    {(() => {
                        const entry = MODE_ICONS[mode] ?? MODE_ICONS.bim;
                        const ModeIcon = entry.Icon;
                        return (
                            <Box
                                as="span"
                                display="inline-flex"
                                alignItems="center"
                                color="icon.base"
                                cursor="default"
                                title={t(entry.labelKey)}
                                aria-label={t(entry.labelKey)}
                            >
                                <ModeIcon size={13} />
                            </Box>
                        );
                    })()}
                </HStack>
            </Flex>
        </Box>
    );
}
